// src/features/auth/authSlice.ts

import {
  createSlice,
  type PayloadAction,
} from '@reduxjs/toolkit';


export type AuthUser = {
  userName: string;
  userId: string;
  userNickname?: string;
  storeId?: number;
  profileImageUrl?: string | null;
  role?: string;
  point?: number;
};


export type AuthState = {
  accessToken: string | null;
  refreshToken: string | null;
  tokenType: string;
  accessTokenExpiresIn: number | null;
  user: AuthUser | null;
};


export type TokenPayload = {
  accessToken?: string | null;
  refreshToken?: string | null;
  tokenType?: string;
  accessTokenExpiresIn?: number | null;
};


const REFRESH_TOKEN_KEY =
  'refreshToken';


const getStoredRefreshToken =
  (): string | null => {
    try {
      return localStorage.getItem(
        REFRESH_TOKEN_KEY,
      );
    } catch {
      return null;
    }
  };


const initialState: AuthState = {
  /**
   * Access Token은 메모리에만 유지합니다.
   *
   * 새로고침하면 null이 되고
   * Refresh Token으로 다시 발급합니다.
   */
  accessToken: null,

  /**
   * Refresh Token만 localStorage에서 복원
   */
  refreshToken:
    getStoredRefreshToken(),

  tokenType: 'Bearer',

  accessTokenExpiresIn: null,

  user: null,
};


const authSlice =
  createSlice({
    name: 'auth',

    initialState,

    reducers: {
      /**
       * 전달된 토큰 항목만 수정합니다.
       */
      setTokens: (
        state,
        action: PayloadAction<
          TokenPayload | undefined
        >,
      ) => {
        const data =
          action.payload;

        if (!data) {
          return;
        }


        if (
          data.accessToken !==
          undefined
        ) {
          state.accessToken =
            data.accessToken;
        }


        if (
          data.refreshToken !==
          undefined
        ) {
          state.refreshToken =
            data.refreshToken;

          try {
            if (
              data.refreshToken
            ) {
              localStorage.setItem(
                REFRESH_TOKEN_KEY,
                data.refreshToken,
              );
            } else {
              localStorage.removeItem(
                REFRESH_TOKEN_KEY,
              );
            }
          } catch {
            // localStorage 사용 불가능 시 무시
          }
        }


        if (
          data.tokenType !==
          undefined
        ) {
          state.tokenType =
            data.tokenType ||
            'Bearer';
        }


        if (
          data.accessTokenExpiresIn !==
          undefined
        ) {
          state.accessTokenExpiresIn =
            data.accessTokenExpiresIn;
        }
      },


      setUser: (
        state,
        action: PayloadAction<
          AuthUser | null
        >,
      ) => {
        state.user =
          action.payload;
      },


      /**
       * 완전 로그아웃
       */
      clearTokens: (
        state,
      ) => {
        state.accessToken =
          null;

        state.refreshToken =
          null;

        state.tokenType =
          'Bearer';

        state.accessTokenExpiresIn =
          null;

        state.user =
          null;


        try {
          localStorage.removeItem(
            REFRESH_TOKEN_KEY,
          );
        } catch {
          // localStorage 사용 불가능 시 무시
        }
      },
    },
  });


export const {
  setTokens,
  setUser,
  clearTokens,
} =
  authSlice.actions;


export default authSlice.reducer;